import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  BillingService, BillingSummary, SubscriptionTier, PurchasableTier,
} from '../../../core/services/billing.service';
import { PLAN_CATALOG, PlanCard } from '../../../core/plan-catalog';

@Component({
  selector: 'app-billing-settings',
  templateUrl: './billing-settings.component.html',
})
export class BillingSettingsComponent implements OnInit {
  summary: BillingSummary | null = null;
  loading = true;
  loadError: string | null = null;

  /** Set while redirecting to Stripe, or while opening the portal. */
  redirecting: 'checkout' | 'portal' | null = null;
  actionError: string | null = null;

  /**
   * True after returning from a successful Checkout redirect while the server
   * still reports the old tier.
   *
   * The redirect is NOT proof of payment — entitlement is granted only by a
   * verified webhook, which can land a moment after the browser comes back. So
   * we show a processing state and poll server-backed state rather than
   * optimistically flipping the plan locally.
   */
  processing = false;
  private pollsRemaining = 0;

  private static readonly POLL_ATTEMPTS = 6;
  private static readonly POLL_INTERVAL_MS = 2000;

  readonly plans = PLAN_CATALOG;

  /** Plans rendered in the grid — the current plan is always included. */
  get visiblePlans(): PlanCard[] {
    return this.plans.filter(p => p.listed || p.tier === this.currentTier);
  }

  constructor(
    private readonly billing: BillingService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
  ) {}

  ngOnInit(): void {
    const params = this.route.snapshot.queryParamMap;

    if (params.get('checkout') === 'success') {
      this.processing = true;
      this.pollsRemaining = BillingSettingsComponent.POLL_ATTEMPTS;
    } else if (params.get('checkout') === 'canceled') {
      this.actionError = 'Checkout was canceled. Your plan has not changed.';
    }

    this.load();

    // Arrived from plan selection at signup with an upgrade already in mind.
    const intent = params.get('upgrade');
    if (intent === 'PRO' || intent === 'ENTERPRISE') {
      this.startUpgrade(intent);
    }
  }

  get currentTier(): SubscriptionTier {
    return this.summary?.entitledTier ?? 'FREE';
  }

  get currentPlan(): PlanCard {
    return this.plans.find(p => p.tier === this.currentTier) ?? this.plans[0];
  }

  /** Managed (sales-contract) orgs are not self-serve; neither are members. */
  get canUpgrade(): boolean {
    return this.summary?.canUpgrade ?? false;
  }

  get canManageBilling(): boolean {
    return Boolean(this.summary?.hasStripeCustomer && this.summary?.canUpgrade);
  }

  get statusLabel(): string {
    if (!this.summary?.status) return this.currentTier === 'FREE' ? 'Free plan' : 'Active';
    switch (this.summary.status) {
      case 'active':             return 'Active';
      case 'trialing':           return 'Trial';
      case 'past_due':           return 'Payment overdue';
      case 'unpaid':             return 'Unpaid';
      case 'canceled':           return 'Canceled';
      case 'paused':             return 'Paused';
      case 'incomplete':
      case 'incomplete_expired': return 'Payment incomplete';
      default:                   return 'Unknown';
    }
  }

  get statusIsHealthy(): boolean {
    return !this.summary?.billingIssue
      && (!this.summary?.status || ['active', 'trialing'].includes(this.summary.status));
  }

  /**
   * Message shown when a payment has failed.
   *
   * The server keeps `graceUntil` set after the window expires (it anchors the
   * policy so retries cannot re-open a fresh window), so the deadline must be
   * compared against now rather than assumed to be in the future.
   */
  get graceMessage(): string | null {
    if (!this.summary?.billingIssue) return null;
    if (!this.summary.graceUntil) {
      return 'We could not process your last payment. Update your payment method to restore access.';
    }

    const until = new Date(this.summary.graceUntil);
    if (Number.isNaN(until.getTime())) return null;

    if (until.getTime() > Date.now()) {
      return `We could not process your last payment. Pro features stay active until `
        + `${until.toLocaleDateString()}. Update your payment method to avoid losing access.`;
    }
    return `We could not process your payment, and Pro features have been suspended. `
      + `Update your payment method to restore access.`;
  }

  isSelfServe(plan: PlanCard): boolean {
    return plan.selfServe;
  }

  /** Only owners/admins of an eligible free org see an upgrade action. */
  showUpgradeButton(plan: PlanCard): boolean {
    return plan.selfServe && plan.tier !== this.currentTier && this.canUpgrade;
  }

  startUpgrade(tier: SubscriptionTier): void {
    // Only the purchasable tiers can start Checkout. The server enforces this
    // too — this guard just avoids a pointless round trip.
    if ((tier !== 'PRO' && tier !== 'ENTERPRISE') || this.redirecting) return;

    this.redirecting = 'checkout';
    this.actionError = null;

    this.billing.createCheckoutSession(tier as PurchasableTier).subscribe({
      next: ({ url }) => {
        // Hand off to Stripe-hosted Checkout. No card data ever touches this app.
        window.location.assign(url);
      },
      error: (err: { error?: { message?: string } }) => {
        this.actionError = err?.error?.message ?? 'Could not start checkout. Please try again.';
        this.redirecting = null;
      },
    });
  }

  openPortal(): void {
    if (this.redirecting) return;

    this.redirecting = 'portal';
    this.actionError = null;

    this.billing.createPortalSession().subscribe({
      next: ({ url }) => window.location.assign(url),
      error: (err: { error?: { message?: string } }) => {
        this.actionError = err?.error?.message ?? 'Could not open the billing portal. Please try again.';
        this.redirecting = null;
      },
    });
  }

  private load(): void {
    this.billing.getSubscription().subscribe({
      next: ({ subscription }) => {
        this.summary = subscription;
        this.loading = false;

        if (this.processing) this.evaluateProcessingState();
      },
      error: () => {
        this.loadError = 'Failed to load subscription details. Please refresh the page.';
        this.loading = false;
        this.processing = false;
      },
    });
  }

  /**
   * Keeps the "processing" banner up until the webhook has actually landed.
   *
   * Gives up after a bounded number of attempts rather than spinning forever —
   * a genuinely stuck webhook should surface as a support-able message, not an
   * infinite spinner.
   */
  private evaluateProcessingState(): void {
    if (this.summary?.entitlementSource === 'STRIPE' && this.summary.entitledTier !== 'FREE') {
      this.processing = false;
      this.clearCheckoutQueryParam();
      return;
    }

    if (this.pollsRemaining-- <= 0) {
      this.processing = false;
      this.actionError = 'Your payment is still being confirmed. This page will update automatically — '
        + 'refresh in a moment, or contact support if it persists.';
      this.clearCheckoutQueryParam();
      return;
    }

    setTimeout(() => this.load(), BillingSettingsComponent.POLL_INTERVAL_MS);
  }

  /** Drops ?checkout= so a refresh does not re-enter the processing state. */
  private clearCheckoutQueryParam(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { checkout: null, session_id: null, upgrade: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }
}
